<template>
  <picture :class="rootClasses" class="oimage" v-bind="$attrs">
    <source
      v-if="desktop"
      :srcset="srcSet('desktop')"
      :media="`(min-width: ${customBreakpoint ? customBreakpoint : screenBreakpoints.md}px)`"
    />
    <source
      v-if="mobile"
      :srcset="srcSet('mobile')"
      :media="`(max-width: ${customBreakpoint ? customBreakpoint : screenBreakpoints.md}px)`"
    />
    <img v-bind="imgBind" />
  </picture>
</template>

<script>
import { ScreenBreakpoints } from '@/enums/general'

export default {
  name: 'OImage',
  inheritAttrs: false,
  props: {
    src: {
      type: String,
      default: ''
    },
    desktop: {
      type: String,
      default: ''
    },
    mobile: {
      type: String,
      default: ''
    },
    alt: {
      type: String,
      default: ''
    },
    noImage: {
      type: Boolean,
      default: false
    },
    imgClass: {
      type: String,
      default: ''
    },
    customBreakpoint: {
      type: Number,
      default: null
    },
    imgStyle: {
      type: [String, Object],
      default: ''
    },
    size: {
      type: String,
      default: 'contain',
      validator: (value) => ['contain', 'cover'].includes(value)
    },
    position: {
      type: String,
      default: 'center',
      validator: (value) => ['center', 'left', 'top', 'bottom', 'right'].includes(value)
    },
    styles: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [],
  data() {
    return {
      screenBreakpoints: ScreenBreakpoints
    }
  },
  computed: {
    rootClasses() {
      return [
        ...{
          true: ['oimage--flex', 'oimage--flex-center'],
          false: []
        }[this.noImage],
        ...{
          contain: ['oimage--contain'],
          cover: ['oimage--cover']
        }[this.size],
        ...{
          left: ['oimage--left'],
          top: ['oimage--top'],
          right: ['oimage--right'],
          bottom: ['oimage--bottom'],
          center: []
        }[this.position]
      ]
    },
    imageOptions() {
      if (this.noImage) {
        return {
          src: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==',
          alt: '',
          role: 'presentation',
          class: 'oimage__placeholder-icon'
        }
      }

      const options = {}

      if (this.src) {
        options.src = this.src
      }
      if (this.alt) {
        options.alt = this.alt
      } else {
        options.role = 'presentation'
      }
      if (this.imgClass) {
        options.class = [this.imgClass]
      }
      if (this.imgStyle) {
        options.style = [this.imgStyle]
      }

      return options
    },
    imgBind() {
      const merged = { ...this.$attrs, ...this.imageOptions }

      const mergedClass = []
      if (this.$attrs && this.$attrs.class) {
        if (Array.isArray(this.$attrs.class)) {
          mergedClass.push(...this.$attrs.class)
        } else {
          mergedClass.push(this.$attrs.class)
        }
      }
      if (this.imageOptions.class) {
        if (Array.isArray(this.imageOptions.class)) {
          mergedClass.push(...this.imageOptions.class)
        } else {
          mergedClass.push(this.imageOptions.class)
        }
      }
      if (mergedClass.length) {
        merged.class = mergedClass
      }

      const mergedStyle = []
      if (this.imageOptions.style) {
        if (Array.isArray(this.imageOptions.style)) {
          mergedStyle.push(...this.imageOptions.style)
        } else {
          mergedStyle.push(this.imageOptions.style)
        }
      }
      if (this.styles) {
        if (Array.isArray(this.styles)) {
          mergedStyle.push(...this.styles)
        } else {
          mergedStyle.push(this.styles)
        }
      }
      if (mergedStyle.length) {
        merged.style = mergedStyle
      }

      return merged
    }
  },
  methods: {
    srcSet(device) {
      return this[device]
    }
  }
}
</script>

<style lang="postcss" scoped>
.oimage {
  &__placeholder-icon {
    background: #fff;
    width: 80px;
    height: 80px;
  }

  &--flex {
    display: flex;
    flex-wrap: wrap;
    height: 100%;
    width: 100%;

    &-center {
      justify-content: center;
      align-content: center;
    }
  }

  &--cover {
    display: flex;

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  &--contain {
    img {
      object-fit: contain;
    }

    .object-cover {
      object-fit: cover;
    }
  }

  &--left {
    img {
      object-position: left;
    }
  }

  &--top {
    img {
      object-position: top;
    }
  }

  &--right {
    img {
      object-position: right;
    }
  }

  &--bottom {
    img {
      object-position: bottom;
    }
  }
}
</style>
